import type { FieldType } from './types.js';

export const KEY_ATTRIBUTE = 'data-siteflow-key';
export const REPEATER_ATTRIBUTE = 'data-siteflow-repeater';

export interface ScannedField {
  el: Element;
  key: string;
  /** Author-declared type; the response type wins when the two disagree. */
  type: FieldType | null;
  /** Target attribute for the value instead of the element's text. */
  attr: string | null;
}

export interface ScannedRepeater {
  el: Element;
  key: string;
}

export interface ScanResult {
  fields: ScannedField[];
  repeaters: ScannedRepeater[];
  /** Every key the page asks for, de-duplicated and in document order. */
  keys: string[];
}

function elementsWith(root: ParentNode, attribute: string): Element[] {
  const found = Array.from(root.querySelectorAll(`[${attribute}]`));

  // `querySelectorAll` never matches the root itself, but callers routinely pass
  // a section element that carries the attribute it is scoped to.
  const self = root as Element;
  if (typeof self.hasAttribute === 'function' && self.hasAttribute(attribute)) {
    found.unshift(self);
  }
  return found;
}

export function scan(root: ParentNode): ScanResult {
  const fields: ScannedField[] = [];
  const repeaters: ScannedRepeater[] = [];
  const keys: string[] = [];

  for (const el of elementsWith(root, KEY_ATTRIBUTE)) {
    const key = el.getAttribute(KEY_ATTRIBUTE);
    if (!key) continue;
    fields.push({
      el,
      key,
      type: (el.getAttribute('data-siteflow-type') as FieldType | null) ?? null,
      attr: el.getAttribute('data-siteflow-attr'),
    });
    if (keys.indexOf(key) === -1) keys.push(key);
  }

  for (const el of elementsWith(root, REPEATER_ATTRIBUTE)) {
    const key = el.getAttribute(REPEATER_ATTRIBUTE);
    if (!key) continue;
    repeaters.push({ el, key });
    if (keys.indexOf(key) === -1) keys.push(key);
  }

  return { fields, repeaters, keys };
}
